import { api, type Recording, type Reaction } from "../lib/api.js";
import { formatBytes } from "../lib/uploader.js";

/**
 * Recording detail: the timeline where a streamer checks and corrects the
 * boundaries the worker derived, then generates clips.
 *
 * The correction path is the point of this screen. Tone detection is good but
 * not infallible, and when it comes back uncertain the alternative to fixing it
 * here is a folder of misaligned clips with no way to explain why.
 */

const hms = (ms: number) => {
  const t = Math.max(0, Math.floor(ms / 1000));
  return [Math.floor(t / 3600), Math.floor(t / 60) % 60, t % 60]
    .map((n) => String(n).padStart(2, "0"))
    .join(":");
};

const parseHms = (s: string): number | null => {
  const parts = s.trim().split(":").map(Number);
  if (parts.some(Number.isNaN)) return null;
  const [h, m, sec] = parts.length === 3 ? parts : [0, ...parts];
  return ((h ?? 0) * 3600 + (m ?? 0) * 60 + (sec ?? 0)) * 1000;
};

export async function recordingView(root: HTMLElement, id: string) {
  root.innerHTML = `<div class="loading">Loading recording…</div>`;

  const { recording, reactions } = await api<{ recording: Recording; reactions: Reaction[] }>(
    "GET",
    `/v1/recordings/${id}`,
  );

  // The streamer's output defaults, so the panel below opens pre-set the way
  // their account is configured. A failed read just leaves everything off.
  const [capDef, refDef, brandDef] = await Promise.all([
    api<{ enabled: boolean }>("GET", "/v1/captions").catch(() => ({ enabled: false })),
    api<{ enabled: boolean }>("GET", "/v1/reframe").catch(() => ({ enabled: false })),
    api<{ enabled: boolean; logo_present: boolean }>("GET", "/v1/brand").catch(() => ({
      enabled: false,
      logo_present: false,
    })),
  ]);

  const duration = recording.duration_ms ?? Math.max(...reactions.map((r) => r.end_ms), 1);
  const lowConfidence = (recording.sync_confidence ?? 0) < 0.55;

  // Moments the streamer never marked, from the two signals that notice one
  // anyway: a burst of audience reactions, and the streamer's own voice going
  // well above their normal. Offered for approval, never clipped silently —
  // audience reaction can be gamed, and a loud streamer is loud for reasons
  // that have nothing to do with the moment, so a human still decides.
  const marked = reactions.filter((r) => !r.suggested);
  const suggested = reactions.filter((r) => r.suggested);
  const crowd = (r: Reaction) =>
    r.audience_score > 0
      ? `<span class="crowd" title="${r.audience_score} audience reactions here">🔥 ${r.audience_score}</span>`
      : "";

  // Why each suggestion is here. An unexplained suggestion is noise: "you
  // shouted here" is a claim the streamer can check against their own memory,
  // and that is what makes the list worth reading at all.
  //
  // Null means a suggestion made before the column existed. Every one of those
  // came from an audience peak, because that was the only signal there was, so
  // reading null as the audience is accurate rather than a guess.
  const SIGNALS = {
    audience: { icon: "🔥", why: "Your audience reacted here" },
    voice: { icon: "🎙️", why: "You reacted here — your mic went well above your normal" },
    both: { icon: "⭐", why: "Your audience reacted here, and so did you" },
  } as const;
  const signalOf = (r: Reaction) => SIGNALS[r.suggested_by ?? "audience"];

  // For a voice-only suggestion the crowd score is zero, and that zero is a
  // fact rather than a gap: the audience did not react here. "🔥 0" would read
  // as a failure, so the row says where it actually came from.
  const evidence = (r: Reaction) =>
    r.suggested_by === "voice"
      ? `<span class="crowd" title="Detected from your microphone, on your own machine">you</span>`
      : crowd(r);

  const signals = new Set(suggested.map((r) => r.suggested_by ?? "audience"));
  const suggestedHeading =
    signals.size !== 1
      ? "Moments you didn't mark"
      : signals.has("voice")
        ? "You reacted to these 🎙️"
        : signals.has("both")
          ? "You and your crowd both reacted to these ⭐"
          : "The crowd marked these 🔥";

  root.innerHTML = `
    <a class="back" href="#/recordings">← Recordings</a>
    <header class="detail-head">
      <div>
        <h1>${recording.filename ?? "Recording"}</h1>
        <p class="meta">
          ${recording.recorded_at ? new Date(recording.recorded_at).toLocaleString() : "—"}
          · ${recording.size_bytes ? formatBytes(Number(recording.size_bytes)) : "—"}
          · ${reactions.length} reactions
        </p>
      </div>
      <button class="btn primary" id="genAll" ${reactions.length ? "" : "disabled"}>
        Generate all clips
      </button>
    </header>

    ${
      // This recording is not here. Studio cut the clips on the streamer's own
      // machine and sent only those, so everything below still works — the
      // boundaries, the re-cuts — but it happens over there, when Studio is
      // next open. Without this the buttons look identical to the uploaded
      // case and the wait is unexplained.
      recording.status === "local"
        ? `<div class="notice">
             <strong>This stream stayed on your PC.</strong>
             Stream-Cut Studio cut your clips there and sent just those — which is
             why this was minutes rather than hours. You can still change a
             boundary and ask for a clip again below; Studio picks it up the next
             time you open it on the machine that holds the recording.
           </div>`
        : ""
    }

    ${
      // The quietest failure in the product: with no session there are no
      // events, so no reactions — and the recording still finishes "ready".
      // Say so plainly rather than leaving someone staring at an empty list.
      !recording.session_id
        ? `<div class="notice warn">
             <strong>This recording isn't linked to a stream session.</strong>
             Without the event log from Stream-Cut Studio there are no reaction
             boundaries to cut from, so no clips can be generated. That usually means
             it was recorded without Studio running, or uploaded long after the
             session ended.
           </div>`
        : ""
    }

    ${
      lowConfidence
        ? `<div class="notice warn">
             <strong>Sync marker not found with confidence.</strong>
             The boundaries below may be offset. Play the recording, find the white flash near the
             start, and enter its timecode to align everything at once.
             <div class="align-row">
               <input id="alignInput" placeholder="00:00:42" value="${
                 recording.sync_offset_ms ? hms(recording.sync_offset_ms) : ""
               }">
               <button class="btn" id="alignBtn">Align</button>
             </div>
           </div>`
        : `<div class="notice ok">
             Sync marker found at ${hms(recording.sync_offset_ms ?? 0)}
             (confidence ${((recording.sync_confidence ?? 0) * 100).toFixed(0)}%${
               recording.drift_ratio && Math.abs(recording.drift_ratio - 1) > 0.00005
                 ? `, drift corrected ${((recording.drift_ratio - 1) * 100).toFixed(3)}%`
                 : ""
             }).
             <button class="link" id="realign">Adjust</button>
           </div>`
    }

    <section class="output card" id="output">
      <h2>Output</h2>
      <p class="meta">How these clips are exported. Pre-set from your account defaults —
        change them here for this stream without changing your defaults.</p>
      <div class="output-opts">
        <label class="toggle">
          <input type="checkbox" id="optVertical" ${refDef.enabled ? "checked" : ""}>
          Vertical 9:16 <span class="muted">(TikTok, Reels, Shorts)</span>
        </label>
        <label class="toggle">
          <input type="checkbox" id="optCaptions" ${capDef.enabled ? "checked" : ""}>
          Burn-in captions
        </label>
        <label class="toggle ${brandDef.logo_present ? "" : "is-disabled"}">
          <input type="checkbox" id="optLogo" ${brandDef.enabled ? "checked" : ""}
            ${brandDef.logo_present ? "" : "disabled"}>
          My logo
          ${brandDef.logo_present ? "" : `<span class="muted">(upload one in Studio first)</span>`}
        </label>
      </div>
    </section>

    <div class="timeline" id="timeline">
      <div class="track">
        ${reactions
          .map(
            (r) => `<div class="span${r.edited_by_user ? " edited" : ""}"
                         style="left:${(r.start_ms / duration) * 100}%;
                                width:${((r.end_ms - r.start_ms) / duration) * 100}%"
                         title="#${r.ordinal} ${hms(r.start_ms)}–${hms(r.end_ms)}"></div>`,
          )
          .join("")}
      </div>
      <div class="ruler"><span>00:00:00</span><span>${hms(duration)}</span></div>
    </div>

    <table class="rx">
      <thead><tr><th>#</th><th>Video</th><th>Start</th><th>End</th><th>Length</th><th></th></tr></thead>
      <tbody id="rxBody">
        ${marked
          .map(
            (r) => `<tr data-id="${r.id}">
              <td class="n">${r.ordinal}</td>
              <td>${r.video_title ?? "—"}${
                r.origin_author ? `<span class="attr">${r.origin_author}</span>` : ""
              }${crowd(r)}</td>
              <td><input class="tc" data-field="start" value="${hms(r.start_ms)}"></td>
              <td><input class="tc" data-field="end" value="${hms(r.end_ms)}"></td>
              <td class="len">${hms(r.end_ms - r.start_ms)}</td>
              <td><button class="btn small" data-clip="${r.id}">Clip</button></td>
            </tr>`,
          )
          .join("")}
      </tbody>
    </table>
    <p class="hint">Edited boundaries are kept if the recording is re-analysed or re-aligned.</p>

    ${
      suggested.length
        ? `<section class="suggested">
             <h2>${suggestedHeading}</h2>
             <p class="hint">Moments you didn't mark, found from your audience's reactions and
               from your own microphone. Nothing is clipped until you approve it.</p>
             <table class="rx">
               <thead><tr><th></th><th>Reactions</th><th>Start</th><th>End</th><th>Length</th><th></th></tr></thead>
               <tbody>
                 ${suggested
                   .map(
                     (r) => `<tr data-id="${r.id}" class="is-suggested">
                       <td class="n" title="${signalOf(r).why}">${signalOf(r).icon}</td>
                       <td>${evidence(r)}</td>
                       <td><input class="tc" data-field="start" value="${hms(r.start_ms)}"></td>
                       <td><input class="tc" data-field="end" value="${hms(r.end_ms)}"></td>
                       <td class="len">${hms(r.end_ms - r.start_ms)}</td>
                       <td><button class="btn small primary" data-clip="${r.id}">Approve &amp; clip</button></td>
                     </tr>`,
                   )
                   .join("")}
               </tbody>
             </table>
           </section>`
        : ""
    }
  `;

  // --- Boundary editing ---------------------------------------------------

  root.querySelectorAll<HTMLInputElement>("input.tc").forEach((input) => {
    input.addEventListener("change", async () => {
      const row = input.closest("tr")!;
      const rid = row.dataset.id!;
      const ms = parseHms(input.value);
      if (ms === null) {
        input.classList.add("invalid");
        return;
      }
      input.classList.remove("invalid");

      const field = input.dataset.field === "start" ? "start_ms" : "end_ms";
      try {
        const updated = await api<Reaction>("PATCH", `/v1/reactions/${rid}`, { [field]: ms });
        input.value = hms(field === "start_ms" ? updated.start_ms : updated.end_ms);
        row.querySelector(".len")!.textContent = hms(updated.end_ms - updated.start_ms);
      } catch (err) {
        input.classList.add("invalid");
        console.error(err);
      }
    });
  });

  // The output choices to send with every generate request, read live off the
  // panel so a change takes effect on the next click without a reload.
  const checked = (sel: string) => root.querySelector<HTMLInputElement>(sel)?.checked ?? false;
  const outputBody = () => ({
    vertical: checked("#optVertical"),
    captions: checked("#optCaptions"),
    logo: checked("#optLogo"),
  });

  root.querySelectorAll<HTMLButtonElement>("[data-clip]").forEach((btn) => {
    btn.addEventListener("click", async () => {
      btn.disabled = true;
      const res = await api<{ cut_by?: string }>(
        "POST",
        `/v1/reactions/${btn.dataset.clip}/clip`,
        outputBody(),
      );
      // Said rather than assumed: the server tells us who is cutting it, and
      // "Queued" on a clip nothing here is going to touch is the wrong word.
      btn.textContent = res?.cut_by === "studio" ? "In Studio" : "Queued";
    });
  });

  const genAll = root.querySelector<HTMLButtonElement>("#genAll");
  genAll?.addEventListener("click", async () => {
    genAll.disabled = true;
    genAll.textContent = "Queueing…";
    const r = await api<{ queued: number; skipped: number }>(
      "POST",
      `/v1/recordings/${id}/clips`,
      outputBody(),
    );
    genAll.textContent =
      recording.status === "local"
        ? `${r.queued} for Studio`
        : `${r.queued} queued`;
    setTimeout(() => (location.hash = "#/clips"), 800);
  });

  // --- Alignment ----------------------------------------------------------

  const align = async (value: string) => {
    const ms = parseHms(value);
    if (ms === null) return;
    await api("PATCH", `/v1/recordings/${id}/align`, { sync_offset_ms: ms });
    recordingView(root, id); // re-render with shifted boundaries
  };

  root.querySelector("#alignBtn")?.addEventListener("click", () => {
    align(root.querySelector<HTMLInputElement>("#alignInput")!.value);
  });

  root.querySelector("#realign")?.addEventListener("click", () => {
    const value = prompt(
      "Timecode of the white flash in your recording (hh:mm:ss)",
      hms(recording.sync_offset_ms ?? 0),
    );
    if (value) align(value);
  });
}
